import {
	Link,
	Outlet,
	createRootRoute,
	createRoute,
	createRouter,
	lazyRouteComponent,
	redirect,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import { PageLoader } from "./components/ui/PageLoader.tsx";
import { useLatinDateInputs } from "./lib/latin-date-inputs.ts";
import { reportClientError } from "./lib/observability.ts";
import { useClinicEvents } from "./lib/useClinicEvents.ts";

// --- Lazily-loaded route components (code-split per route) ---
const ForgotPasswordPage = lazyRouteComponent(
	() => import("./features/auth/ForgotPasswordPage.tsx"),
	"ForgotPasswordPage",
);
const AdminLoginPage = lazyRouteComponent(
	() => import("./features/admin/AdminLoginPage.tsx"),
	"AdminLoginPage",
);
const AdminDashboardPage = lazyRouteComponent(
	() => import("./features/admin/AdminDashboardPage.tsx"),
	"AdminDashboardPage",
);
const AdminFacilityDetailPage = lazyRouteComponent(
	() => import("./features/admin/AdminFacilityDetailPage.tsx"),
	"AdminFacilityDetailPage",
);
const LabWalkInPage = lazyRouteComponent(
	() => import("./features/lab/LabWalkInPage.tsx"),
	"LabWalkInPage",
);
const HomeCollectionsPage = lazyRouteComponent(
	() => import("./features/lab/HomeCollectionsPage.tsx"),
	"HomeCollectionsPage",
);
const RevenuePage = lazyRouteComponent(
	() => import("./features/revenue/RevenuePage.tsx"),
	"RevenuePage",
);
const OutstandingReferralsPage = lazyRouteComponent(
	() => import("./features/clinical/OutstandingReferralsPage.tsx"),
	"OutstandingReferralsPage",
);
const LabOrderResultPage = lazyRouteComponent(
	() => import("./features/lab/LabOrderResultPage.tsx"),
	"LabOrderResultPage",
);
const CriticalAlertsPage = lazyRouteComponent(
	() => import("./features/lab/CriticalAlertsPage.tsx"),
	"CriticalAlertsPage",
);
const ResultsInboxPage = lazyRouteComponent(
	() => import("./features/clinical/ResultsInboxPage.tsx"),
	"ResultsInboxPage",
);
const RadiologyWorklistPage = lazyRouteComponent(
	() => import("./features/radiology/RadiologyWorklistPage.tsx"),
	"RadiologyWorklistPage",
);
const RadiologyReportPage = lazyRouteComponent(
	() => import("./features/radiology/RadiologyReportPage.tsx"),
	"RadiologyReportPage",
);
const IncomingRadiologyOrdersPage = lazyRouteComponent(
	() => import("./features/radiology/IncomingRadiologyOrdersPage.tsx"),
	"IncomingRadiologyOrdersPage",
);
const RadiologyCriticalFindingsPage = lazyRouteComponent(
	() => import("./features/radiology/RadiologyCriticalFindingsPage.tsx"),
	"RadiologyCriticalFindingsPage",
);
const RadiologyTatReportPage = lazyRouteComponent(
	() => import("./features/radiology/RadiologyTatReportPage.tsx"),
	"RadiologyTatReportPage",
);
const PatientsListPage = lazyRouteComponent(
	() => import("./features/patients/PatientsListPage.tsx"),
	"PatientsListPage",
);
const PatientNewPage = lazyRouteComponent(
	() => import("./features/patients/PatientNewPage.tsx"),
	"PatientNewPage",
);
const ImportPatientsPage = lazyRouteComponent(
	() => import("./features/patients/ImportPatientsPage.tsx"),
	"ImportPatientsPage",
);
const PatientDetailPage = lazyRouteComponent(
	() => import("./features/patients/PatientDetailPage.tsx"),
	"PatientDetailPage",
);
const AppointmentsListPage = lazyRouteComponent(
	() => import("./features/appointments/AppointmentsListPage.tsx"),
	"AppointmentsListPage",
);
const AppointmentNewPage = lazyRouteComponent(
	() => import("./features/appointments/AppointmentNewPage.tsx"),
	"AppointmentNewPage",
);
const ClinicWalkInPage = lazyRouteComponent(
	() => import("./features/appointments/ClinicWalkInPage.tsx"),
	"ClinicWalkInPage",
);
const AppointmentDetailPage = lazyRouteComponent(
	() => import("./features/appointments/AppointmentDetailPage.tsx"),
	"AppointmentDetailPage",
);
const LandingPage = lazyRouteComponent(
	() => import("./features/landing/LandingPage.tsx"),
	"LandingPage",
);
const WaitlistPage = lazyRouteComponent(
	() => import("./features/appointments/WaitlistPage.tsx"),
	"WaitlistPage",
);
const CheckoutPage = lazyRouteComponent(
	() => import("./features/appointments/CheckoutPage.tsx"),
	"CheckoutPage",
);
const CheckInPage = lazyRouteComponent(
	() => import("./features/appointments/CheckInPage.tsx"),
	"CheckInPage",
);
const DaySchedulePage = lazyRouteComponent(
	() => import("./features/appointments/DaySchedulePage.tsx"),
	"DaySchedulePage",
);
const RemindersQueuePage = lazyRouteComponent(
	() => import("./features/reminders/RemindersQueuePage.tsx"),
	"RemindersQueuePage",
);
const SettingsLayout = lazyRouteComponent(
	() => import("./features/settings/SettingsLayout.tsx"),
	"SettingsLayout",
);
const ClinicSettingsPage = lazyRouteComponent(
	() => import("./features/settings/ClinicSettingsPage.tsx"),
	"ClinicSettingsPage",
);
const SchedulePage = lazyRouteComponent(
	() => import("./features/settings/SchedulePage.tsx"),
	"SchedulePage",
);
const ServicesPage = lazyRouteComponent(
	() => import("./features/settings/ServicesPage.tsx"),
	"ServicesPage",
);
const TreatmentPackagesSettingsPage = lazyRouteComponent(
	() => import("./features/settings/TreatmentPackagesSettingsPage.tsx"),
	"TreatmentPackagesSettingsPage",
);
const MedicationsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/MedicationsSettingsPage.tsx"),
	"MedicationsSettingsPage",
);
const LabTestsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/LabTestsSettingsPage.tsx"),
	"LabTestsSettingsPage",
);
const QcSettingsPage = lazyRouteComponent(
	() => import("./features/settings/QcSettingsPage.tsx"),
	"QcSettingsPage",
);
const ReflexRulesSettingsPage = lazyRouteComponent(
	() => import("./features/settings/ReflexRulesSettingsPage.tsx"),
	"ReflexRulesSettingsPage",
);
const InstrumentsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/InstrumentsSettingsPage.tsx"),
	"InstrumentsSettingsPage",
);
const RadiologyTemplatesSettingsPage = lazyRouteComponent(
	() => import("./features/settings/RadiologyTemplatesSettingsPage.tsx"),
	"RadiologyTemplatesSettingsPage",
);
const QcDailyPage = lazyRouteComponent(
	() => import("./features/lab/QcDailyPage.tsx"),
	"QcDailyPage",
);
const TatReportPage = lazyRouteComponent(
	() => import("./features/lab/TatReportPage.tsx"),
	"TatReportPage",
);
const AccessionLabelPage = lazyRouteComponent(
	() => import("./features/lab/AccessionLabelPage.tsx"),
	"AccessionLabelPage",
);
const InventoryPage = lazyRouteComponent(
	() => import("./features/lab/InventoryPage.tsx"),
	"InventoryPage",
);
const FinancePage = lazyRouteComponent(
	() => import("./features/finance/FinancePage.tsx"),
	"FinancePage",
);
const PayrollPage = lazyRouteComponent(
	() => import("./features/finance/PayrollPage.tsx"),
	"PayrollPage",
);
const InvoiceDetailPage = lazyRouteComponent(
	() => import("./features/finance/InvoiceDetailPage.tsx"),
	"InvoiceDetailPage",
);
const TasksPage = lazyRouteComponent(
	() => import("./features/tasks/TasksPage.tsx"),
	"TasksPage",
);
const BranchesPage = lazyRouteComponent(
	() => import("./features/branches/BranchesPage.tsx"),
	"BranchesPage",
);
const ReviewsPage = lazyRouteComponent(
	() => import("./features/reviews/ReviewsPage.tsx"),
	"ReviewsPage",
);
const PublicReviewPage = lazyRouteComponent(
	() => import("./features/reviews/PublicReviewPage.tsx"),
	"PublicReviewPage",
);
const LabPanelsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/LabPanelsSettingsPage.tsx"),
	"LabPanelsSettingsPage",
);
const LabReferralsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/LabReferralsSettingsPage.tsx"),
	"LabReferralsSettingsPage",
);
const ReferralCommissionsSettingsPage = lazyRouteComponent(
	() => import("./features/settings/ReferralCommissionsSettingsPage.tsx"),
	"ReferralCommissionsSettingsPage",
);
const InvoicesSettingsPage = lazyRouteComponent(
	() => import("./features/settings/InvoicesSettingsPage.tsx"),
	"InvoicesSettingsPage",
);
const AuditLogPage = lazyRouteComponent(
	() => import("./features/settings/AuditLogPage.tsx"),
	"AuditLogPage",
);
const DiagnosesReportPage = lazyRouteComponent(
	() => import("./features/settings/DiagnosesReportPage.tsx"),
	"DiagnosesReportPage",
);
const StaffSettingsPage = lazyRouteComponent(
	() => import("./features/settings/StaffSettingsPage.tsx"),
	"StaffSettingsPage",
);
const CommissionPage = lazyRouteComponent(
	() => import("./features/settings/CommissionPage.tsx"),
	"CommissionPage",
);
const RemindersPage = lazyRouteComponent(
	() => import("./features/settings/RemindersPage.tsx"),
	"RemindersPage",
);
const LoyaltySettingsPage = lazyRouteComponent(
	() => import("./features/settings/LoyaltySettingsPage.tsx"),
	"LoyaltySettingsPage",
);
const SubscriptionSettingsPage = lazyRouteComponent(
	() => import("./features/settings/SubscriptionSettingsPage.tsx"),
	"SubscriptionSettingsPage",
);
const SecurityPage = lazyRouteComponent(
	() => import("./features/settings/SecurityPage.tsx"),
	"SecurityPage",
);
const PortalLoginPage = lazyRouteComponent(
	() => import("./features/patient-portal/PortalLoginPage.tsx"),
	"PortalLoginPage",
);
const PortalPage = lazyRouteComponent(
	() => import("./features/patient-portal/PortalPage.tsx"),
	"PortalPage",
);
const AppointmentActionPage = lazyRouteComponent(
	() => import("./features/appointment-action/AppointmentActionPage.tsx"),
	"AppointmentActionPage",
);
const PublicClinicPage = lazyRouteComponent(
	() => import("./features/public-clinic/PublicClinicPage.tsx"),
	"PublicClinicPage",
);
const QueueDisplayPage = lazyRouteComponent(
	() => import("./features/queue/QueueDisplayPage.tsx"),
	"QueueDisplayPage",
);
const RecallsPage = lazyRouteComponent(
	() => import("./features/recalls/RecallsPage.tsx"),
	"RecallsPage",
);
const PhotoFollowupsPage = lazyRouteComponent(
	() => import("./features/recalls/PhotoFollowupsPage.tsx"),
	"PhotoFollowupsPage",
);
const InpatientPage = lazyRouteComponent(
	() => import("./features/inpatient/InpatientPage.tsx"),
	"InpatientPage",
);
const PublicPrescriptionPage = lazyRouteComponent(
	() => import("./features/public-rx/PublicPrescriptionPage.tsx"),
	"PublicPrescriptionPage",
);
const PublicLabOrderPage = lazyRouteComponent(
	() => import("./features/public-lab/PublicLabOrderPage.tsx"),
	"PublicLabOrderPage",
);
const PublicRadiologyReportPage = lazyRouteComponent(
	() => import("./features/public-radiology/PublicRadiologyReportPage.tsx"),
	"PublicRadiologyReportPage",
);
const PublicReceiptPage = lazyRouteComponent(
	() => import("./features/public-receipt/PublicReceiptPage.tsx"),
	"PublicReceiptPage",
);
const AcceptInvitationPage = lazyRouteComponent(
	() => import("./features/invite/AcceptInvitationPage.tsx"),
	"AcceptInvitationPage",
);
import {
	type Language,
	businessTypeSchema,
	getLocalized,
	slugify,
} from "@klyra/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	ChevronDown,
	Compass,
	FlaskConical,
	KeyRound,
	MoreHorizontal,
	Pill,
	Scan,
	Search,
	Stethoscope,
} from "lucide-react";
import {
	type FormEvent,
	Suspense,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { GlobalSearch } from "./components/GlobalSearch.tsx";
import { InstallPrompt } from "./components/InstallPrompt.tsx";
import { LanguageToggle } from "./components/LanguageToggle.tsx";
import {
	HamburgerButton,
	MobileNavDrawer,
} from "./components/MobileNavDrawer.tsx";
import { NavItemBadge } from "./components/NavItemBadge.tsx";
import { NotificationBell } from "./components/NotificationBell.tsx";
import { OfflineIndicator } from "./components/OfflineIndicator.tsx";
import { QuickCreateMenu } from "./components/QuickCreateMenu.tsx";
import { ThemeToggle } from "./components/ThemeToggle.tsx";
import { TurnstileWidget } from "./components/TurnstileWidget.tsx";
import { UpdateBanner } from "./components/UpdateBanner.tsx";
import { Button } from "./components/ui/Button.tsx";
import { Brand, Card } from "./components/ui/Card.tsx";
import { Input } from "./components/ui/Input.tsx";
import { AssistantButton } from "./features/assistant/AssistantButton.tsx";
import { BranchSwitcher } from "./features/branches/BranchSwitcher.tsx";
import { HttpError, auth } from "./lib/api.ts";
import {
	type NavItem as NavItemT,
	navItemsWithCapabilities,
	primaryNavItems,
} from "./lib/nav.ts";
import { refreshToLatest } from "./lib/sw-update.ts";
import { useAdminStore } from "./stores/admin-store.ts";
import { useAuthStore } from "./stores/auth-store.ts";

// =====================================================================
// Root layout
// =====================================================================

const rootRoute = createRootRoute({
	component: function RootLayout() {
		const { t } = useTranslation();
		const { user, clinic, clearSession, impersonation, hasPermission } =
			useAuthStore();
		const navItems = navItemsWithCapabilities(clinic?.facility_type, {
			inpatient: clinic?.inpatient_enabled === true,
			role: clinic?.role,
			hasPermission,
			businessType: clinic?.business_type,
		});
		const navigate = useNavigate();
		const routerState = useRouterState();
		const isAuthed = Boolean(user);
		// Realtime updates (notifications + critical alerts). No-ops when logged
		// out; polling stays as the fallback.
		useClinicEvents();
		// Force every native date/time input to English Latin LTR (DATE POLICY).
		useLatinDateInputs();
		const [mobileNavOpen, setMobileNavOpen] = useState(false);
		const [searchOpen, setSearchOpen] = useState(false);
		// Patient portal flows use a slim, distraction-free shell — no staff nav,
		// no AI assistant button. Same for the public appointment-action URLs.
		const isPortalArea =
			routerState.location.pathname.startsWith("/portal") ||
			routerState.location.pathname.startsWith("/p/") ||
			routerState.location.pathname.startsWith("/a/") ||
			routerState.location.pathname.startsWith("/c/") ||
			routerState.location.pathname.startsWith("/q/") ||
			routerState.location.pathname.startsWith("/rx/") ||
			routerState.location.pathname.startsWith("/lab/") ||
			routerState.location.pathname.startsWith("/receipt/") ||
			routerState.location.pathname.startsWith("/invite/");
		// TV display + the public prescription/lab/receipt views are full-bleed:
		// no chrome, no nav, no footer. The patient opening /rx|/lab|/receipt
		// on their phone shouldn't see the staff header at all.
		// The marketing landing page owns its own header/footer — the shell
		// would otherwise stack a second set on top of it.
		const isFullScreenDisplay =
			routerState.location.pathname === "/" ||
			routerState.location.pathname.startsWith("/q/") ||
			routerState.location.pathname.startsWith("/rx/") ||
			routerState.location.pathname.startsWith("/lab/") ||
			routerState.location.pathname.startsWith("/receipt/") ||
			routerState.location.pathname.startsWith("/admin");

		const handleHeaderLogout = () => {
			clearSession();
			navigate({ to: "/login" });
		};

		// Cmd/Ctrl+K opens the global search. Disabled while typing inside
		// contenteditable fields (we don't want to swallow the user's key
		// combo if they're mid-edit elsewhere).
		const showStaffControlsForShortcut = isAuthed && !isPortalArea;
		useEffect(() => {
			if (!showStaffControlsForShortcut) return;
			function onKey(e: KeyboardEvent) {
				if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
					e.preventDefault();
					setSearchOpen(true);
				}
			}
			window.addEventListener("keydown", onKey);
			return () => window.removeEventListener("keydown", onKey);
		}, [showStaffControlsForShortcut]);

		if (isFullScreenDisplay) {
			return <Outlet />;
		}

		const showStaffControls = isAuthed && !isPortalArea;

		return (
			<div
				className={`min-h-dvh flex flex-col bg-background ${
					showStaffControls
						? "pb-[calc(3.5rem+env(safe-area-inset-bottom))] sm:pb-0"
						: ""
				}`}
			>
				{/* Keyboard a11y: jump past the nav chrome straight to content. */}
				<a
					href="#main-content"
					className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:start-2 focus:rounded-lg focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
				>
					{t("nav.skip_to_content")}
				</a>
				{showStaffControls && impersonation && (
					<div className="bg-amber-500 text-amber-950 text-sm px-3 py-2 flex items-center justify-between gap-2 sticky top-0 z-40">
						<span className="font-medium truncate inline-flex items-center gap-1.5">
							<KeyRound
								className="size-4 shrink-0"
								strokeWidth={2}
								aria-hidden
							/>{" "}
							وضع المعاينة: {impersonation.facility_name}
						</span>
						<button
							type="button"
							onClick={() => {
								clearSession();
								navigate({ to: "/admin" });
							}}
							className="shrink-0 rounded-md bg-amber-950/15 hover:bg-amber-950/25 px-3 py-1 font-medium"
						>
							خروج من المعاينة
						</button>
					</div>
				)}
				<header
					className="print:hidden border-b border-border bg-card px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between sticky top-0 z-30 gap-2"
					style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}
				>
					<div className="flex items-center gap-2 sm:gap-6 min-w-0">
						{showStaffControls && (
							<HamburgerButton
								onClick={() => setMobileNavOpen(true)}
								ariaLabel={t("nav.open_menu")}
							/>
						)}
						<Link
							to={isAuthed && !isPortalArea ? "/dashboard" : "/"}
							className="min-w-0"
						>
							<Brand compactOnMobile />
						</Link>
						{showStaffControls && (
							<nav className="hidden sm:flex items-center gap-1 text-sm">
								{/* Inline chips: daily-loop primaries always; `desktop` tier
                    only from xl — a 13-item nav doesn't fit a 1366px laptop.
                    Everything else lives in the "More" dropdown so no item
                    is ever clipped or unreachable. */}
								{navItems
									.filter((item) => item.primary || item.desktop)
									.map((item) =>
										// Primary chips always inline; `desktop`-tier items hide
										// below xl. Visibility is controlled on a `display:contents`
										// WRAPPER, never via a `hidden` utility on the link itself —
										// NavLink hardcodes `inline-flex`, and two `display` utilities
										// at equal specificity let source order (inline-flex) defeat
										// `hidden`, so the desktop chips leaked in and crammed the bar
										// on sub-xl laptops. `contents` keeps the link a direct flex
										// item (gap intact) while the wrapper owns show/hide.
										item.primary ? (
											<NavLink
												key={item.to}
												to={item.to}
												label={t(item.i18nKey)}
												badge={<NavItemBadge item={item} />}
											/>
										) : (
											<span key={item.to} className="hidden xl:contents">
												<NavLink
													to={item.to}
													label={t(item.i18nKey)}
													badge={<NavItemBadge item={item} />}
												/>
											</span>
										),
									)}
								<NavOverflowMenu
									items={navItems.filter((item) => !item.primary)}
								/>
							</nav>
						)}
					</div>
					<div className="flex items-center gap-1 sm:gap-2 shrink-0">
						{showStaffControls && <BranchSwitcher />}
						{showStaffControls && (
							<QuickCreateMenu
								facilityType={clinic?.facility_type}
								businessType={clinic?.business_type}
							/>
						)}
						{showStaffControls && (
							<button
								type="button"
								onClick={() => setSearchOpen(true)}
								className="inline-flex items-center justify-center w-10 h-10 sm:w-auto sm:h-9 sm:px-3 sm:gap-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
								aria-label={t("search.open_button", { defaultValue: "بحث" })}
							>
								<Search className="size-5" strokeWidth={2} aria-hidden />
								<kbd className="hidden sm:inline text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
									Ctrl+K
								</kbd>
							</button>
						)}
						{showStaffControls && <NotificationBell />}
						{/* Theme + language live in the mobile drawer to keep the top bar
                uncluttered; shown inline only from sm up. */}
						<div
							className={
								showStaffControls
									? "hidden sm:flex items-center gap-1 sm:gap-2"
									: "flex items-center gap-1 sm:gap-2"
							}
						>
							<ThemeToggle />
							<LanguageToggle />
						</div>
						{showStaffControls && (
							<Button
								variant="ghost"
								size="sm"
								onClick={handleHeaderLogout}
								className="hidden sm:inline-flex"
							>
								{t("nav.logout")}
							</Button>
						)}
					</div>
				</header>

				{showStaffControls && <InstallPrompt />}

				{showStaffControls && (
					<MobileNavDrawer
						open={mobileNavOpen}
						onClose={() => setMobileNavOpen(false)}
						onLogout={handleHeaderLogout}
						facilityType={clinic?.facility_type}
					/>
				)}

				{showStaffControls && (
					<GlobalSearch
						open={searchOpen}
						onClose={() => setSearchOpen(false)}
					/>
				)}

				<main id="main-content" className="flex-1 flex flex-col">
					{/* Keyed by path so each navigation replays a subtle fade-in,
              giving route changes a polished feel without a transition lib. */}
					<div
						key={routerState.location.pathname}
						className="flex-1 flex flex-col animate-fade-in"
					>
						<Outlet />
					</div>
				</main>
				{!isPortalArea && clinic?.feature_flags?.voice_assistant !== false && (
					<AssistantButton />
				)}
				<footer
					className={`print:hidden border-t border-border bg-card px-4 py-3 text-center text-xs text-muted-foreground ${showStaffControls ? "hidden sm:block" : ""}`}
				>
					© 2026 Klyra — {t("app.tagline")}
					<span className="mx-1.5">·</span>
					<Link
						to="/legal/$doc"
						params={{ doc: "terms" }}
						className="hover:underline"
					>
						{t("app.legal_terms")}
					</Link>
					<span className="mx-1.5">·</span>
					<Link
						to="/legal/$doc"
						params={{ doc: "privacy" }}
						className="hover:underline"
					>
						{t("app.legal_privacy")}
					</Link>
				</footer>
				{/* Mobile-only bottom tab bar for thumb-reach navigation (native feel).
            Shows the 4 daily-loop essentials + a "More" button that opens the
            full drawer. Desktop keeps the top nav. */}
				{showStaffControls && (
					<BottomTabBar
						items={primaryNavItems(
							clinic?.facility_type,
							clinic?.role,
							hasPermission,
							clinic?.business_type,
						)}
						onMore={() => setMobileNavOpen(true)}
					/>
				)}
				{showStaffControls && <OfflineIndicator />}
				{showStaffControls && <UpdateBanner />}
			</div>
		);
	},
});

function BottomTabBar({
	items,
	onMore,
}: { items: ReadonlyArray<NavItemT>; onMore: () => void }) {
	const { t } = useTranslation();
	const routerState = useRouterState();
	const pathname = routerState.location.pathname;
	// `items` is already the curated ≤4 primaries from primaryNavItems().
	// The 5th slot is always a "More" button that opens the full drawer — so
	// every other destination stays one tap away without crowding the bar.
	const shown = items.slice(0, 4);
	// Highlight "More" whenever the active route isn't one of the thumb-bar
	// primaries (i.e. the user is somewhere reached through the drawer).
	const onPrimaryRoute = shown.some(
		(i) => pathname === i.to || pathname.startsWith(`${i.to}/`),
	);
	// 56px touch rows (M3 spec) + an active pill behind the icon so the
	// current tab reads at a glance — color alone is too subtle outdoors.
	const base =
		"flex-1 flex flex-col items-center justify-center gap-0.5 py-1 min-h-14 text-muted-foreground transition-colors";
	const pill = (active: boolean) =>
		`relative inline-flex items-center justify-center rounded-full px-3 py-0.5 transition-colors ${
			active
				? "bg-brand-50 text-brand-700 dark:bg-brand-950/50 dark:text-brand-300"
				: ""
		}`;
	return (
		<nav
			className="sm:hidden print:hidden fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 backdrop-blur"
			style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
			aria-label={t("nav.menu")}
		>
			<div className="flex">
				{shown.map((item) => {
					const active =
						pathname === item.to || pathname.startsWith(`${item.to}/`);
					return (
						<Link
							key={item.to}
							to={item.to}
							className={`${base} ${
								active ? "text-brand-700 dark:text-brand-300 font-semibold" : ""
							}`}
						>
							<span className={pill(active)}>
								<item.icon
									className="size-5 shrink-0"
									strokeWidth={2}
									aria-hidden
								/>
								{item.badge && (
									<span className="absolute -top-1.5 -end-1.5">
										<NavItemBadge item={item} />
									</span>
								)}
							</span>
							<span className="text-xs font-medium leading-tight text-center line-clamp-2">
								{t(item.i18nKey)}
							</span>
						</Link>
					);
				})}
				<button
					type="button"
					onClick={onMore}
					className={`${base} ${
						onPrimaryRoute
							? ""
							: "text-brand-700 dark:text-brand-300 font-semibold"
					}`}
					aria-label={t("nav.open_menu")}
				>
					<span className={pill(!onPrimaryRoute)}>
						<MoreHorizontal
							className="size-5 shrink-0"
							strokeWidth={2}
							aria-hidden
						/>
					</span>
					<span className="text-xs font-medium leading-tight text-center line-clamp-2">
						{t("nav.more")}
					</span>
				</button>
			</div>
		</nav>
	);
}

function NavLink({
	to,
	label,
	badge,
	extraClass,
}: {
	to:
		| "/dashboard"
		| "/check-in"
		| "/appointments"
		| "/reminders"
		| "/waitlist"
		| "/patients"
		| "/critical-alerts"
		| "/qc"
		| "/tat"
		| "/inventory"
		| "/walk-in"
		| "/home-collections"
		| "/revenue"
		| "/referrals-out"
		| "/radiology/worklist"
		| "/radiology/incoming"
		| "/radiology/critical-findings"
		| "/radiology/tat"
		| "/pos"
		| "/pharmacy/sales"
		| "/pharmacy/prescriptions"
		| "/pharmacy/procurement"
		| "/pharmacy/inventory"
		| "/supplies"
		| "/cohort-insights"
		| "/recalls"
		| "/photo-followups"
		| "/tasks"
		| "/clinical/inbox"
		| "/finance"
		| "/reviews"
		| "/inpatient"
		| "/settings";
	label: string;
	/** Optional live-count bubble (e.g. pending incoming prescriptions). */
	badge?: React.ReactNode;
	/** Extra (e.g. responsive display) classes appended in both states. */
	extraClass?: string;
}) {
	const extra = extraClass ? ` ${extraClass}` : "";
	return (
		<Link
			to={to}
			className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground transition-colors${extra}`}
			activeProps={{
				className: `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md whitespace-nowrap bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-medium${extra}`,
			}}
		>
			{label}
			{badge}
		</Link>
	);
}

/**
 * Desktop header "More" dropdown. Receives every non-primary nav item:
 * `desktop`-tier entries are hidden from the menu at xl+ (they're inline
 * chips there), the rest always live here. Mirrors QuickCreateMenu's
 * close-on-outside-click/Escape behaviour.
 */
function NavOverflowMenu({ items }: { items: ReadonlyArray<NavItemT> }) {
	const { t } = useTranslation();
	const routerState = useRouterState();
	const pathname = routerState.location.pathname;
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node))
				setOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	// Close when a navigation happens (item clicked or back/forward).
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger by design
	useEffect(() => {
		setOpen(false);
	}, [pathname]);

	if (items.length === 0) return null;
	// At xl the desktop-tier entries render inline, so if nothing else
	// remains the trigger itself disappears at that breakpoint.
	const hasAlwaysOverflow = items.some((i) => !i.desktop);
	// Highlight the trigger when the active route lives inside the menu —
	// otherwise deep pages reached from here would show no active state.
	// Desktop-tier items are excluded: at xl their inline chip carries the
	// active state, and double-highlighting the trigger would look broken.
	const activeInside = items.some(
		(i) => !i.desktop && (pathname === i.to || pathname.startsWith(`${i.to}/`)),
	);

	return (
		<div
			className={`relative ${hasAlwaysOverflow ? "" : "xl:hidden"}`}
			ref={ref}
		>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-haspopup="menu"
				aria-expanded={open}
				className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md whitespace-nowrap transition-colors ${
					activeInside
						? "bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-medium"
						: "text-muted-foreground hover:bg-muted hover:text-foreground"
				}`}
			>
				{t("nav.more")}
				<ChevronDown
					className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`}
					strokeWidth={2}
					aria-hidden
				/>
			</button>
			{open && (
				<div
					role="menu"
					className="absolute start-0 mt-2 w-56 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-card shadow-xl p-1.5 z-50"
				>
					{items.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							role="menuitem"
							onClick={() => setOpen(false)}
							className={`flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors ${
								item.desktop ? "xl:hidden" : ""
							}`}
							activeProps={{
								className: `flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-medium ${
									item.desktop ? "xl:hidden" : ""
								}`,
							}}
						>
							<item.icon
								className="size-4 shrink-0 text-muted-foreground"
								strokeWidth={2}
								aria-hidden
							/>
							{t(item.i18nKey)}
							<NavItemBadge item={item} />
						</Link>
					))}
				</div>
			)}
		</div>
	);
}

// =====================================================================
// /  →  redirects to /login or /dashboard
// =====================================================================

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	beforeLoad: () => {
		// Signed-in staff go straight to work; everyone else gets the public
		// marketing page (it used to dump anonymous visitors on a login form).
		if (useAuthStore.getState().isAuthenticated()) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: LandingPage,
});

// =====================================================================
// /login
// =====================================================================

const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	beforeLoad: () => {
		if (useAuthStore.getState().isAuthenticated()) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: function LoginPage() {
		const { t } = useTranslation("auth");
		const navigate = useNavigate();
		const setSession = useAuthStore((s) => s.setSession);

		const [email, setEmail] = useState("");
		const [password, setPassword] = useState("");

		// Only surface the "create account" CTA when self-signup is actually open
		// (server-driven), so it never dead-links to a 403 in invite-only mode.
		const configQuery = useQuery({
			queryKey: ["auth-config"],
			queryFn: () => auth.config(),
			staleTime: 5 * 60 * 1000,
			retry: false,
		});
		const publicSignup = configQuery.data?.public_signup === true;

		const mutation = useMutation({
			mutationFn: auth.login,
			onSuccess: (data) => {
				setSession(data);
				navigate({ to: "/dashboard" });
			},
		});

		const onSubmit = (e: FormEvent) => {
			e.preventDefault();
			mutation.mutate({ email: email.trim().toLowerCase(), password });
		};

		const httpError =
			mutation.error instanceof HttpError ? mutation.error : null;
		// A super-admin's password is valid, but they have no clinic membership
		// (the API returns 403 FORBIDDEN). That's not a login failure — they
		// landed on the wrong door. Point them at the platform console instead
		// of showing a scary "unexpected error".
		const isPlatformAccount = httpError?.status === 403;
		const errorKey =
			httpError?.status === 401 || httpError?.error.code === "UNAUTHORIZED"
				? "invalid_credentials"
				: httpError?.status === 423
					? "account_locked"
					: "generic";

		return (
			<div className="flex-1 flex items-center justify-center px-4 py-12 bg-gradient-brand">
				<Card className="w-full max-w-md">
					<div className="mb-6 text-center">
						<div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-gradient-brand text-2xl font-black text-white shadow-lg shadow-brand-600/30">
							K
						</div>
						<h2 className="text-2xl font-bold text-foreground">
							{t("login.title")}
						</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							{t("login.subtitle")}
						</p>
					</div>

					<form onSubmit={onSubmit} className="flex flex-col gap-4">
						<Input
							label={t("login.email")}
							type="email"
							autoComplete="email"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="you@clinic.com"
							dir="ltr"
						/>
						<Input
							label={t("login.password")}
							type="password"
							autoComplete="current-password"
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>

						<div className="-mt-2 text-end">
							<Link
								to="/forgot-password"
								className="text-xs text-muted-foreground hover:text-foreground hover:underline"
							>
								{t("login.forgot_password")}
							</Link>
						</div>

						{mutation.isError && isPlatformAccount && (
							<div className="rounded-lg bg-brand-50 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-900 px-3 py-2.5 text-sm text-brand-800 dark:text-brand-200">
								<p>{t("login.platform_account")}</p>
								<Link
									to="/admin/login"
									className="mt-1 inline-block font-semibold underline"
								>
									{t("login.platform_account_link")}
								</Link>
							</div>
						)}

						{mutation.isError && !isPlatformAccount && (
							<div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-400">
								{t(`errors.${errorKey}`)}
							</div>
						)}

						<Button
							type="submit"
							loading={mutation.isPending}
							fullWidth
							size="lg"
						>
							{t("login.submit")}
						</Button>
					</form>

					{publicSignup && (
						<p className="mt-6 text-center text-sm text-muted-foreground">
							{t("login.no_account")}{" "}
							<Link
								to="/signup"
								className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
							>
								{t("login.create_account")}
							</Link>
						</p>
					)}
				</Card>
			</div>
		);
	},
});

// Account recovery guidance — public (a locked-out user can't be authed).
const forgotPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/forgot-password",
	component: ForgotPasswordPage,
});

// =====================================================================
// /signup
// =====================================================================

const BUSINESS_TYPES = businessTypeSchema.options;

// Email-verification landing page (G5). The verification email links here with
// ?token=…; we POST it, and on success log the owner straight in. On failure
// (expired / invalid / already used) we let them request a fresh link.
const verifyEmailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/verify-email",
	validateSearch: (search: Record<string, unknown>): { token?: string } =>
		typeof search.token === "string" ? { token: search.token } : {},
	component: function VerifyEmailPage() {
		const { t } = useTranslation("auth");
		const navigate = useNavigate();
		const setSession = useAuthStore((s) => s.setSession);
		const { token } = verifyEmailRoute.useSearch();

		const verify = useQuery({
			queryKey: ["verify-email", token],
			queryFn: () => auth.verifyEmail(token ?? ""),
			enabled: Boolean(token),
			retry: false,
			gcTime: 0,
		});

		useEffect(() => {
			if (verify.isSuccess && verify.data) {
				setSession(verify.data);
				navigate({ to: "/dashboard" });
			}
		}, [verify.isSuccess, verify.data, setSession, navigate]);

		const resend = useMutation({
			mutationFn: (email: string) => auth.resendVerification(email),
		});
		const [resendEmail, setResendEmail] = useState("");

		const httpErr = verify.error instanceof HttpError ? verify.error : null;
		const errorMessage = !token
			? t("verify_email.missing_token")
			: httpErr
				? httpErr.status === 410
					? t("verify_email.expired")
					: httpErr.status === 404
						? t("verify_email.invalid")
						: t("errors.generic")
				: null;

		if (token && verify.isFetching) {
			return (
				<div className="flex-1 flex items-center justify-center px-4 py-12">
					<Card className="w-full max-w-md text-center">
						<p className="text-sm text-muted-foreground">
							{t("verify_email.verifying")}
						</p>
					</Card>
				</div>
			);
		}

		if (errorMessage) {
			return (
				<div className="flex-1 flex items-center justify-center px-4 py-12">
					<Card className="w-full max-w-md text-center">
						<h2 className="text-2xl font-bold text-foreground">
							{t("verify_email.failed_title")}
						</h2>
						<p className="mt-3 text-sm text-destructive">{errorMessage}</p>
						<div className="mt-6 space-y-3 text-start">
							<Input
								type="email"
								placeholder={t("verify_email.email_placeholder")}
								value={resendEmail}
								onChange={(e) => setResendEmail(e.target.value)}
							/>
							<Button
								type="button"
								className="w-full"
								disabled={
									resend.isPending || resend.isSuccess || !resendEmail.trim()
								}
								onClick={() => resend.mutate(resendEmail.trim().toLowerCase())}
							>
								{resend.isSuccess
									? t("verify_email.resend_success")
									: t("verify_email.resend_button")}
							</Button>
						</div>
						<Link
							to="/login"
							className="mt-6 inline-block font-medium text-brand-600 hover:text-brand-700 hover:underline"
						>
							{t("verify_email.back_to_login")}
						</Link>
					</Card>
				</div>
			);
		}

		// Success path renders briefly until the effect redirects to the dashboard.
		return (
			<div className="flex-1 flex items-center justify-center px-4 py-12">
				<Card className="w-full max-w-md text-center">
					<p className="text-sm text-muted-foreground">
						{t("verify_email.verifying")}
					</p>
				</Card>
			</div>
		);
	},
});

const signupRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/signup",
	beforeLoad: () => {
		if (useAuthStore.getState().isAuthenticated()) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: function SignupPage() {
		const { t, i18n } = useTranslation("auth");
		const navigate = useNavigate();
		const setSession = useAuthStore((s) => s.setSession);

		const [form, setForm] = useState({
			facility_type: "clinic" as "clinic" | "lab" | "pharmacy" | "radiology",
			clinic_name_ar: "",
			clinic_name_en: "",
			slug: "",
			business_type: "cosmetic" as (typeof BUSINESS_TYPES)[number],
			owner_name: "",
			email: "",
			phone: "",
			password: "",
		});
		// business_type is a clinical specialty — only a clinic picks one; every
		// other facility type gets the neutral 'general'.
		const isClinic = form.facility_type === "clinic";
		const isLab = form.facility_type === "lab";
		const [slugTouched, setSlugTouched] = useState(false);
		// A pure-Arabic facility name slugifies to "" (slugify strips Arabic for
		// URL-safety), which used to leave the REQUIRED latin slug field empty and
		// block signup at the door. This stable fallback guarantees a valid, almost
		// certainly unique slug so an Arabic-only owner never gets stuck.
		const [fallbackSlug] = useState(
			() => `clinic-${Math.random().toString(36).slice(2, 8)}`,
		);
		// When email verification is on, signup returns no session — we show a
		// "check your inbox" step instead of logging in (G5).
		const [verificationEmail, setVerificationEmail] = useState<string | null>(
			null,
		);
		// Captcha token (G6) — only required when the server advertises a site key.
		const [captchaToken, setCaptchaToken] = useState<string | null>(null);
		// Real consent action (blocker #6): the checkbox gates submit, and the
		// server persists when/what was accepted into clinic.settings.consent.
		const [consentChecked, setConsentChecked] = useState(false);

		// Self-signup is server-gated. When closed, show an invite-only notice
		// instead of a form that would only 403 on submit.
		const configQuery = useQuery({
			queryKey: ["auth-config"],
			queryFn: () => auth.config(),
			staleTime: 5 * 60 * 1000,
			retry: false,
		});

		const derivedSlug = useMemo(
			() => slugify(form.clinic_name_en || form.clinic_name_ar),
			[form.clinic_name_en, form.clinic_name_ar],
		);
		// Manual value wins; else the name-derived slug; else the generated
		// fallback (Arabic-only name → derivedSlug is empty). Never empty.
		const effectiveSlug =
			slugTouched && form.slug.trim() ? form.slug : derivedSlug || fallbackSlug;

		const captchaSiteKey = configQuery.data?.captcha_site_key ?? null;

		const mutation = useMutation({
			mutationFn: auth.clinicSignup,
			onSuccess: (data) => {
				// Email verification on → no tokens; show the "check inbox" step.
				if ("verification_required" in data) {
					setVerificationEmail(data.email);
					return;
				}
				setSession(data);
				navigate({ to: "/dashboard" });
			},
		});

		const resendMutation = useMutation({
			mutationFn: () => auth.resendVerification(verificationEmail ?? ""),
		});

		const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
			setForm((p) => ({ ...p, [k]: v }));

		const onSubmit = (e: FormEvent) => {
			e.preventDefault();
			mutation.mutate({
				clinic: {
					name: {
						ar: form.clinic_name_ar.trim(),
						...(form.clinic_name_en.trim()
							? { en: form.clinic_name_en.trim() }
							: {}),
					},
					slug: effectiveSlug,
					facility_type: form.facility_type,
					// Only a clinic carries a clinical specialty; others get 'general'.
					business_type: isClinic ? form.business_type : "general",
					timezone: "Asia/Baghdad",
					default_language: i18n.language.startsWith("ar") ? "ar" : "en",
				},
				owner: {
					full_name: form.owner_name.trim(),
					email: form.email.trim().toLowerCase(),
					...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
					password: form.password,
					preferred_language: i18n.language.startsWith("ar") ? "ar" : "en",
				},
				consent: { terms: true, privacy: true },
				captcha_token: captchaToken ?? undefined,
			});
		};

		const httpErr = mutation.error instanceof HttpError ? mutation.error : null;
		const err = httpErr?.error ?? null;
		const errorMessage = err
			? httpErr?.status === 403
				? // 403 is either "signup closed" or a failed captcha. If a captcha is
					// active, signup is open, so a 403 here means the captcha failed.
					captchaSiteKey
					? t("signup.captcha_failed")
					: t("signup.closed_body")
				: err.code === "CONFLICT" && err.message.toLowerCase().includes("slug")
					? t("errors.slug_taken")
					: err.code === "CONFLICT" &&
							err.message.toLowerCase().includes("email")
						? t("errors.email_exists")
						: err.code === "VALIDATION_FAILED"
							? // Don't surface raw zod text ("Expected string, received null")
								// to a signing-up owner — show a localized generic instead.
								t("errors.generic")
							: t("errors.generic")
			: null;

		// Invite-only mode: show a notice instead of a form that would 403.
		if (configQuery.data && configQuery.data.public_signup !== true) {
			return (
				<div className="flex-1 flex items-center justify-center px-4 py-12">
					<Card className="w-full max-w-md text-center">
						<h2 className="text-2xl font-bold text-foreground">
							{t("signup.closed_title")}
						</h2>
						<p className="mt-2 text-sm text-muted-foreground">
							{t("signup.closed_body")}
						</p>
						<Link
							to="/login"
							className="mt-6 inline-block font-medium text-brand-600 hover:text-brand-700 hover:underline"
						>
							{t("signup.login_link")}
						</Link>
					</Card>
				</div>
			);
		}

		// Email verification step: signup succeeded but the owner must confirm
		// their email before they can log in (G5).
		if (verificationEmail) {
			return (
				<div className="flex-1 flex items-center justify-center px-4 py-12">
					<Card className="w-full max-w-md text-center">
						<h2 className="text-2xl font-bold text-foreground">
							{t("verify_email.check_inbox")}
						</h2>
						<p className="mt-3 text-sm text-muted-foreground">
							{t("verify_email.check_inbox_body", { email: verificationEmail })}
						</p>
						<Button
							type="button"
							variant="secondary"
							className="mt-6 w-full"
							disabled={resendMutation.isPending || resendMutation.isSuccess}
							onClick={() => resendMutation.mutate()}
						>
							{resendMutation.isSuccess
								? t("verify_email.resend_success")
								: resendMutation.isPending
									? t("verify_email.resending")
									: t("verify_email.resend_button")}
						</Button>
						<Link
							to="/login"
							className="mt-6 inline-block font-medium text-brand-600 hover:text-brand-700 hover:underline"
						>
							{t("verify_email.back_to_login")}
						</Link>
					</Card>
				</div>
			);
		}

		return (
			<div className="flex-1 flex items-start justify-center px-4 py-8">
				<Card className="w-full max-w-2xl">
					<div className="mb-6 text-center">
						<h2 className="text-2xl font-bold text-foreground">
							{t("signup.title")}
						</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							{t("signup.subtitle")}
						</p>
					</div>

					<form onSubmit={onSubmit} className="flex flex-col gap-5">
						<section>
							<h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
								{t("signup.facility_type")}
							</h3>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
								{(
									[
										{
											key: "clinic",
											icon: Stethoscope,
											label: t("signup.facility_clinic"),
										},
										{
											key: "lab",
											icon: FlaskConical,
											label: t("signup.facility_lab"),
										},
										{
											key: "pharmacy",
											icon: Pill,
											label: t("signup.facility_pharmacy"),
										},
										{
											key: "radiology",
											icon: Scan,
											label: t("signup.facility_radiology"),
										},
									] as const
								).map((opt) => {
									const active = form.facility_type === opt.key;
									return (
										<button
											key={opt.key}
											type="button"
											onClick={() => set("facility_type", opt.key)}
											aria-pressed={active}
											className={`flex items-center gap-3 rounded-xl border p-3 text-start transition-colors ${
												active
													? "border-brand-500 bg-brand-50 dark:bg-brand-950/40 ring-2 ring-brand-500/40"
													: "border-border hover:bg-muted"
											}`}
										>
											<span
												className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-300" : "bg-muted text-muted-foreground"}`}
											>
												<opt.icon
													className="size-5"
													strokeWidth={2}
													aria-hidden
												/>
											</span>
											<span className="font-medium text-foreground">
												{opt.label}
											</span>
										</button>
									);
								})}
							</div>

							<h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
								{isClinic
									? t("signup.clinic_section")
									: isLab
										? t("signup.lab_section")
										: t("signup.facility_section")}
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Input
									label={
										isClinic
											? t("signup.clinic_name_ar")
											: isLab
												? t("signup.lab_name_ar")
												: t("signup.facility_name_ar")
									}
									required
									value={form.clinic_name_ar}
									onChange={(e) => set("clinic_name_ar", e.target.value)}
									dir="rtl"
									placeholder={
										isClinic
											? "عيادة د. سارة للتجميل"
											: isLab
												? "مختبر الشفاء للتحاليل"
												: form.facility_type === "pharmacy"
													? "صيدلية الشفاء"
													: "مركز الرازي للأشعة"
									}
								/>
								<Input
									label={
										isClinic
											? t("signup.clinic_name_en")
											: isLab
												? t("signup.lab_name_en")
												: t("signup.facility_name_en")
									}
									value={form.clinic_name_en}
									onChange={(e) => set("clinic_name_en", e.target.value)}
									dir="ltr"
									placeholder={
										isClinic
											? "Dr. Sara Cosmetic Clinic"
											: isLab
												? "Al-Shifa Lab"
												: form.facility_type === "pharmacy"
													? "Al-Shifa Pharmacy"
													: "Al-Razi Imaging Center"
									}
								/>
								{/* Slug is auto-derived (or a generated fallback for an
								    Arabic-only name) and tucked behind "advanced" — a new
								    owner never has to think about a URL-safe handle, but a
								    power user can still customise it. */}
								<details className="sm:col-span-2 rounded-lg border border-border/60 px-3 py-2">
									<summary className="cursor-pointer text-sm text-muted-foreground">
										{t("signup.advanced")} ·{" "}
										<span dir="ltr" className="font-mono text-xs">
											/c/{effectiveSlug}
										</span>
									</summary>
									<div className="mt-3">
										<Input
											label={t("signup.slug")}
											value={effectiveSlug}
											onChange={(e) => {
												setSlugTouched(true);
												set("slug", e.target.value);
											}}
											helper={t("signup.slug_helper")}
											dir="ltr"
											pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
										/>
									</div>
								</details>
								{isClinic && (
									<div className="flex flex-col gap-1.5">
										<label
											htmlFor="signup-business-type"
											className="text-sm font-medium text-foreground"
										>
											{t("signup.business_type")}
										</label>
										<select
											id="signup-business-type"
											className="h-10 rounded-lg border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
											value={form.business_type}
											onChange={(e) =>
												set(
													"business_type",
													e.target.value as (typeof BUSINESS_TYPES)[number],
												)
											}
										>
											{BUSINESS_TYPES.map((bt) => (
												<option key={bt} value={bt}>
													{t(`business_types.${bt}`, { ns: "common" })}
												</option>
											))}
										</select>
									</div>
								)}
							</div>
						</section>

						<hr className="border-border" />

						<section>
							<h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
								{t("signup.owner_section")}
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Input
									label={t("signup.owner_name")}
									required
									minLength={2}
									value={form.owner_name}
									onChange={(e) => set("owner_name", e.target.value)}
									placeholder={t("signup.owner_name_placeholder")}
								/>
								<Input
									label={t("signup.email")}
									type="email"
									required
									value={form.email}
									onChange={(e) => set("email", e.target.value)}
									placeholder="you@clinic.com"
									dir="ltr"
								/>
								<Input
									label={t("signup.phone")}
									type="tel"
									value={form.phone}
									onChange={(e) => set("phone", e.target.value)}
									placeholder={t("signup.phone_placeholder")}
									dir="ltr"
								/>
								<Input
									label={t("signup.password")}
									type="password"
									required
									minLength={12}
									value={form.password}
									onChange={(e) => set("password", e.target.value)}
									helper={t("signup.password_helper")}
								/>
							</div>
						</section>

						{errorMessage && (
							<div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-400">
								{errorMessage}
							</div>
						)}

						{captchaSiteKey && (
							<TurnstileWidget
								siteKey={captchaSiteKey}
								onToken={setCaptchaToken}
							/>
						)}

						<label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
							<input
								type="checkbox"
								checked={consentChecked}
								onChange={(e) => setConsentChecked(e.target.checked)}
								required
								className="mt-1 size-4 accent-brand-600"
							/>
							<span>
								{t("signup.consent_prefix")}{" "}
								<Link
									to="/legal/$doc"
									params={{ doc: "terms" }}
									target="_blank"
									className="text-brand-600 hover:underline"
								>
									{t("signup.consent_terms")}
								</Link>{" "}
								{t("signup.consent_and")}{" "}
								<Link
									to="/legal/$doc"
									params={{ doc: "privacy" }}
									target="_blank"
									className="text-brand-600 hover:underline"
								>
									{t("signup.consent_privacy")}
								</Link>
							</span>
						</label>

						<Button
							type="submit"
							loading={mutation.isPending}
							disabled={
								!consentChecked || (Boolean(captchaSiteKey) && !captchaToken)
							}
							size="lg"
							fullWidth
						>
							{mutation.isPending ? t("signup.submitting") : t("signup.submit")}
						</Button>
					</form>

					<p className="mt-6 text-center text-sm text-muted-foreground">
						{t("signup.have_account")}{" "}
						<Link
							to="/login"
							className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
						>
							{t("signup.login_link")}
						</Link>
					</p>
				</Card>
			</div>
		);
	},
});

// =====================================================================
// /dashboard  (protected)
// =====================================================================

const DashboardPage = lazyRouteComponent(
	() => import("./features/dashboard/DashboardPage.tsx"),
	"DashboardPage",
);
const LabDashboardPage = lazyRouteComponent(
	() => import("./features/lab/LabDashboardPage.tsx"),
	"LabDashboardPage",
);
const PharmacyDashboardPage = lazyRouteComponent(
	() => import("./features/pharmacy/PharmacyDashboardPage.tsx"),
	"PharmacyDashboardPage",
);
const PosPage = lazyRouteComponent(
	() => import("./features/pharmacy/PosPage.tsx"),
	"PosPage",
);
const PharmacySalesPage = lazyRouteComponent(
	() => import("./features/pharmacy/PharmacySalesPage.tsx"),
	"PharmacySalesPage",
);
const ProcurementPage = lazyRouteComponent(
	() => import("./features/pharmacy/ProcurementPage.tsx"),
	"ProcurementPage",
);
const PharmacyInventoryPage = lazyRouteComponent(
	() => import("./features/pharmacy/PharmacyInventoryPage.tsx"),
	"PharmacyInventoryPage",
);
const IncomingPrescriptionsPage = lazyRouteComponent(
	() => import("./features/pharmacy/IncomingPrescriptionsPage.tsx"),
	"IncomingPrescriptionsPage",
);
const SuppliesPage = lazyRouteComponent(
	() => import("./features/clinical/SuppliesPage.tsx"),
	"SuppliesPage",
);
const CohortInsightsPage = lazyRouteComponent(
	() => import("./features/clinical/CohortInsightsPage.tsx"),
	"CohortInsightsPage",
);
const RadiologyDashboardPage = lazyRouteComponent(
	() => import("./features/radiology/RadiologyDashboardPage.tsx"),
	"RadiologyDashboardPage",
);

// The dashboard is facility-aware: lab + pharmacy + radiology tenants get
// their own home screen, everything else (clinic) keeps the original dashboard.
// Each dashboard is its own lazy chunk, so the Suspense boundary shows the
// route loader while the right one streams in.
function DashboardRouter() {
	const facilityType = useAuthStore((s) => s.clinic?.facility_type);
	const Dashboard =
		facilityType === "lab"
			? LabDashboardPage
			: facilityType === "pharmacy"
				? PharmacyDashboardPage
				: facilityType === "radiology"
					? RadiologyDashboardPage
					: DashboardPage;
	return (
		<Suspense fallback={<PageLoader />}>
			<Dashboard />
		</Suspense>
	);
}

const dashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/dashboard",
	beforeLoad: () => {
		if (!useAuthStore.getState().isAuthenticated()) {
			throw redirect({ to: "/login" });
		}
	},
	component: DashboardRouter,
});

function _Detail({
	label,
	value,
	dir,
}: { label: string; value: string; dir?: "ltr" | "rtl" }) {
	return (
		<div>
			<dt className="text-xs uppercase text-muted-foreground">{label}</dt>
			<dd
				className="mt-0.5 text-foreground font-mono text-xs break-all"
				dir={dir}
			>
				{value}
			</dd>
		</div>
	);
}

// =====================================================================
// Router export
// =====================================================================

// =====================================================================
// /patients (protected)  +  /patients/new  +  /patients/$patientId
// =====================================================================

const authGuard = () => {
	if (!useAuthStore.getState().isAuthenticated()) {
		throw redirect({ to: "/login" });
	}
};

// Finance / payroll / P&L is billing.view-gated server-side. A role without it
// (nurse, plain doctor) used to reach the page and dead-end on a silent 403 —
// bounce them to the dashboard instead. The nav already hides the entry; this
// covers a typed URL or a stale bookmark.
const billingViewGuard = () => {
	authGuard();
	if (!useAuthStore.getState().hasPermission("billing.view")) {
		throw redirect({ to: "/dashboard" });
	}
};

const patientsListRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/patients",
	beforeLoad: authGuard,
	component: PatientsListPage,
});

const patientNewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/patients/new",
	beforeLoad: authGuard,
	component: PatientNewPage,
	// intent=schedule → after creating, jump straight to booking with the new
	// patient pre-selected (skip the land-on-detail-then-re-search detour).
	validateSearch: (search: Record<string, unknown>): { intent?: string } =>
		search.intent === "schedule" ? { intent: "schedule" } : {},
});

const patientsImportRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/patients/import",
	beforeLoad: authGuard,
	component: ImportPatientsPage,
});

const PATIENT_TABS = ["summary", "clinical", "visits", "labs", "docs"] as const;
const patientDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/patients/$patientId",
	beforeLoad: authGuard,
	// Optional ?tab= lets other screens deep-link straight to a section
	// (e.g. the outstanding-referrals tracker jumps a ready result to "labs").
	validateSearch: (search: Record<string, unknown>): { tab?: string } => {
		const tab = search.tab;
		return typeof tab === "string" &&
			(PATIENT_TABS as ReadonlyArray<string>).includes(tab)
			? { tab }
			: {};
	},
	component: PatientDetailPage,
});

const appointmentsListRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/appointments",
	beforeLoad: authGuard,
	component: AppointmentsListPage,
});

const checkInRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/check-in",
	beforeLoad: authGuard,
	component: CheckInPage,
});

const dayScheduleRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/calendar",
	beforeLoad: authGuard,
	component: DaySchedulePage,
});

const remindersQueueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reminders",
	beforeLoad: authGuard,
	component: RemindersQueuePage,
});

// Lab walk-in order builder (standalone-lab facing). Top-level path —
// /lab/* is the public result permalink, so we use /walk-in.
const labWalkInRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/walk-in",
	beforeLoad: authGuard,
	component: LabWalkInPage,
});

// Clinic one-screen walk-in: register + start the visit in a single flow.
const clinicWalkInRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/clinic-walk-in",
	beforeLoad: authGuard,
	// Optional patient_id pre-selects the patient (e.g. "start visit" from the
	// patient record) so the doctor skips the search step.
	validateSearch: (search: Record<string, unknown>): { patient_id?: string } =>
		typeof search.patient_id === "string"
			? { patient_id: search.patient_id }
			: {},
	component: ClinicWalkInPage,
});

// Lab home-collection dispatch board (standalone-lab facing).
const homeCollectionsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/home-collections",
	beforeLoad: authGuard,
	component: HomeCollectionsPage,
});

// Facility daily revenue (lab + radiology).
const revenueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/revenue",
	beforeLoad: authGuard,
	component: RevenuePage,
});

// Clinic-side outstanding referrals tracker.
const outstandingReferralsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/referrals-out",
	beforeLoad: authGuard,
	component: OutstandingReferralsPage,
});

// Lab order result-entry page (lab tech enters values, then completes).
const labOrderResultRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/lab-order/$orderId",
	beforeLoad: authGuard,
	component: LabOrderResultPage,
});

// Critical-value alerts workspace (ISO 15189 §5.8.7 notification log).
const criticalAlertsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/critical-alerts",
	beforeLoad: authGuard,
	component: CriticalAlertsPage,
});

// Doctor's unified results-review inbox (critical + abnormal + radiology).
const resultsInboxRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/clinical/inbox",
	beforeLoad: authGuard,
	component: ResultsInboxPage,
});

// Radiology routes (mounted only when facility_type === 'radiology').
const radiologyWorklistRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/radiology/worklist",
	beforeLoad: authGuard,
	component: RadiologyWorklistPage,
});
const radiologyReportRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/radiology/order/$orderId",
	beforeLoad: authGuard,
	component: RadiologyReportPage,
});
const radiologyIncomingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/radiology/incoming",
	beforeLoad: authGuard,
	component: IncomingRadiologyOrdersPage,
});
const radiologyCriticalFindingsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/radiology/critical-findings",
	beforeLoad: authGuard,
	component: RadiologyCriticalFindingsPage,
});
const radiologyTatRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/radiology/tat",
	beforeLoad: authGuard,
	component: RadiologyTatReportPage,
});

// Pharmacy POS (mounted for facility_type === 'pharmacy').
const posRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pos",
	beforeLoad: authGuard,
	component: PosPage,
});
const pharmacySalesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pharmacy/sales",
	beforeLoad: authGuard,
	component: PharmacySalesPage,
});
const procurementRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pharmacy/procurement",
	beforeLoad: authGuard,
	component: ProcurementPage,
});
const pharmacyInventoryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pharmacy/inventory",
	beforeLoad: authGuard,
	component: PharmacyInventoryPage,
});
// Incoming referred prescriptions (pharmacy ⟵ linked clinics).
const incomingPrescriptionsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pharmacy/prescriptions",
	beforeLoad: authGuard,
	component: IncomingPrescriptionsPage,
});
const suppliesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/supplies",
	beforeLoad: authGuard,
	component: SuppliesPage,
});
const cohortInsightsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/cohort-insights",
	beforeLoad: authGuard,
	component: CohortInsightsPage,
});
const recallsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/recalls",
	beforeLoad: authGuard,
	component: RecallsPage,
});

// Cosmetic/dermatology photo-progress worklist (mirrors recalls).
const photoFollowupsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/photo-followups",
	beforeLoad: authGuard,
	component: PhotoFollowupsPage,
});

// Daily QC routine (ISO 15189 §5.6 Levey-Jennings + Westgard).
const qcDailyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/qc",
	beforeLoad: authGuard,
	component: QcDailyPage,
});

// TAT report (ISO 15189 §5.6.5 turnaround-time quality indicator).
const tatReportRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tat",
	beforeLoad: authGuard,
	component: TatReportPage,
});

// Accession label printer (ISO 15189 §5.4.4 specimen reception).
const accessionLabelRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/label/$orderId",
	beforeLoad: authGuard,
	component: AccessionLabelPage,
});

// Reagent inventory (ISO 15189 §5.3).
const inventoryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/inventory",
	beforeLoad: authGuard,
	component: InventoryPage,
});

// Finance: expenses + P&L + daily cash report.
const financeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/finance",
	beforeLoad: billingViewGuard,
	component: FinancePage,
});

// Payroll: staff salaries + disbursements (sits under finance conceptually).
const payrollRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/finance/payroll",
	beforeLoad: billingViewGuard,
	component: PayrollPage,
});

// Standalone invoice detail — pay / refund / share / cancel any invoice
// (visit-linked, package, or legacy orphan). Every invoice list links here.
const invoiceDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/invoices/$invoiceId",
	beforeLoad: authGuard,
	component: InvoiceDetailPage,
});

// Unified staff task inbox.
const tasksRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tasks",
	beforeLoad: authGuard,
	component: TasksPage,
});

const inpatientRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/inpatient",
	// Auth + capability gate: the HIS is opt-in per clinic. Nav already hides it
	// when disabled, but a typed/bookmarked URL must not reach the full module.
	beforeLoad: () => {
		authGuard();
		if (useAuthStore.getState().clinic?.inpatient_enabled !== true) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: InpatientPage,
});

// Multi-branch consolidated report (chain rollup).
const branchesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/branches",
	beforeLoad: authGuard,
	component: BranchesPage,
});

// Reviews / reputation dashboard.
const reviewsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reviews",
	beforeLoad: authGuard,
	component: ReviewsPage,
});

// ---- Platform admin (separate super-admin session) ----
const adminLoginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/login",
	beforeLoad: () => {
		if (useAdminStore.getState().isAuthenticated())
			throw redirect({ to: "/admin" });
	},
	component: AdminLoginPage,
});

const adminDashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin",
	beforeLoad: () => {
		if (!useAdminStore.getState().isAuthenticated())
			throw redirect({ to: "/admin/login" });
	},
	component: AdminDashboardPage,
});

const adminFacilityDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/facilities/$facilityId",
	beforeLoad: () => {
		if (!useAdminStore.getState().isAuthenticated())
			throw redirect({ to: "/admin/login" });
	},
	component: AdminFacilityDetailPage,
});

const appointmentNewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/appointments/new",
	beforeLoad: authGuard,
	component: AppointmentNewPage,
});

const appointmentDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/appointments/$appointmentId",
	beforeLoad: authGuard,
	component: AppointmentDetailPage,
});

// Reception checkout — collect payment for an appointment without routing the
// front desk through the doctor's clinical encounter page.
// Reception waitlist — patients waiting for an earlier slot (freed-slot flow).
const waitlistRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/waitlist",
	beforeLoad: authGuard,
	component: WaitlistPage,
});

const checkoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/checkout/$appointmentId",
	beforeLoad: authGuard,
	component: CheckoutPage,
});

const settingsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/settings",
	beforeLoad: authGuard,
	component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/",
	beforeLoad: () => {
		throw redirect({ to: "/settings/clinic" });
	},
});

const settingsClinicRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/clinic",
	component: ClinicSettingsPage,
});

const settingsScheduleRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/schedule",
	component: SchedulePage,
});

const settingsServicesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/services",
	component: ServicesPage,
});

const settingsPackagesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/packages",
	component: TreatmentPackagesSettingsPage,
});

const settingsMedicationsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/medications",
	component: MedicationsSettingsPage,
});

const settingsLabTestsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/lab-tests",
	component: LabTestsSettingsPage,
});

const settingsLabPanelsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/lab-panels",
	component: LabPanelsSettingsPage,
});

const settingsReferralsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/referrals",
	component: LabReferralsSettingsPage,
});

const settingsReferralCommissionsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/referral-commissions",
	component: ReferralCommissionsSettingsPage,
});

const settingsQcRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/qc",
	component: QcSettingsPage,
});

const settingsReflexRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/reflex",
	component: ReflexRulesSettingsPage,
});

const settingsInstrumentsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/instruments",
	component: InstrumentsSettingsPage,
});

const settingsRadiologyTemplatesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/radiology-templates",
	component: RadiologyTemplatesSettingsPage,
});

const settingsInvoicesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/invoices",
	component: InvoicesSettingsPage,
});

const settingsAuditRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/audit",
	component: AuditLogPage,
});

const settingsDiagnosesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/diagnoses",
	component: DiagnosesReportPage,
});

const settingsStaffRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/staff",
	component: StaffSettingsPage,
});

const settingsCommissionRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/commission",
	component: CommissionPage,
});

const settingsRemindersRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/reminders",
	component: RemindersPage,
});

const settingsSecurityRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/security",
	component: SecurityPage,
});

const settingsLoyaltyRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/loyalty",
	component: LoyaltySettingsPage,
});

const settingsSubscriptionRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: "/subscription",
	component: SubscriptionSettingsPage,
});

// ---- Patient portal (unguarded — token is the auth) ----
import { usePortalStore } from "./stores/portal-store.ts";

const portalLoginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/p/$token",
	component: PortalLoginPage,
});

const portalRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/portal",
	beforeLoad: () => {
		if (!usePortalStore.getState().isAuthenticated()) {
			throw redirect({ to: "/" });
		}
	},
	component: PortalPage,
});

const appointmentActionRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/a/$token",
	component: AppointmentActionPage,
});

const publicClinicRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/c/$slug",
	component: PublicClinicPage,
});

const queueDisplayRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/q/$slug",
	component: QueueDisplayPage,
});

const publicRxRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/rx/$token",
	component: PublicPrescriptionPage,
});

const publicLabRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/lab/$token",
	component: PublicLabOrderPage,
});

const publicRadiologyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/rad/$token",
	component: PublicRadiologyReportPage,
});

const publicReceiptRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/receipt/$token",
	component: PublicReceiptPage,
});

const publicReviewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/review/$slug",
	component: PublicReviewPage,
});

const acceptInvitationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/invite/$token",
	component: AcceptInvitationPage,
});

// ---- Legal documents (public) ----
const LegalPage = lazyRouteComponent(
	() => import("./features/legal/LegalPage.tsx"),
	"LegalPage",
);
const legalRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/legal/$doc",
	component: LegalPage,
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	loginRoute,
	forgotPasswordRoute,
	signupRoute,
	verifyEmailRoute,
	dashboardRoute,
	patientsListRoute,
	patientNewRoute,
	patientsImportRoute,
	patientDetailRoute,
	appointmentsListRoute,
	appointmentNewRoute,
	appointmentDetailRoute,
	waitlistRoute,
	checkoutRoute,
	checkInRoute,
	dayScheduleRoute,
	remindersQueueRoute,
	labWalkInRoute,
	clinicWalkInRoute,
	homeCollectionsRoute,
	revenueRoute,
	outstandingReferralsRoute,
	labOrderResultRoute,
	criticalAlertsRoute,
	resultsInboxRoute,
	radiologyWorklistRoute,
	radiologyReportRoute,
	radiologyIncomingRoute,
	radiologyCriticalFindingsRoute,
	radiologyTatRoute,
	posRoute,
	pharmacySalesRoute,
	procurementRoute,
	pharmacyInventoryRoute,
	incomingPrescriptionsRoute,
	suppliesRoute,
	cohortInsightsRoute,
	recallsRoute,
	photoFollowupsRoute,
	qcDailyRoute,
	tatReportRoute,
	accessionLabelRoute,
	inventoryRoute,
	financeRoute,
	payrollRoute,
	invoiceDetailRoute,
	tasksRoute,
	inpatientRoute,
	branchesRoute,
	reviewsRoute,
	adminLoginRoute,
	adminDashboardRoute,
	adminFacilityDetailRoute,
	portalLoginRoute,
	portalRoute,
	appointmentActionRoute,
	publicClinicRoute,
	queueDisplayRoute,
	publicRxRoute,
	publicLabRoute,
	publicRadiologyRoute,
	publicReceiptRoute,
	publicReviewRoute,
	acceptInvitationRoute,
	legalRoute,
	settingsRoute.addChildren([
		settingsIndexRoute,
		settingsClinicRoute,
		settingsScheduleRoute,
		settingsServicesRoute,
		settingsPackagesRoute,
		settingsMedicationsRoute,
		settingsLabTestsRoute,
		settingsLabPanelsRoute,
		settingsReferralsRoute,
		settingsReferralCommissionsRoute,
		settingsQcRoute,
		settingsReflexRoute,
		settingsInstrumentsRoute,
		settingsRadiologyTemplatesRoute,
		settingsInvoicesRoute,
		settingsAuditRoute,
		settingsDiagnosesRoute,
		settingsStaffRoute,
		settingsCommissionRoute,
		settingsRemindersRoute,
		settingsSecurityRoute,
		settingsLoyaltyRoute,
		settingsSubscriptionRoute,
	]),
]);

function RouteError({ error }: { error: Error }) {
	reportClientError({
		message: error?.message || "route error",
		stack: error?.stack,
		source: "error_boundary",
	});
	return (
		<div className="min-h-[60vh] flex items-center justify-center p-6">
			<div className="w-full max-w-sm text-center rounded-2xl border border-border bg-card p-6 shadow-card">
				<div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-xl">
					<AlertTriangle className="size-6" strokeWidth={2} aria-hidden />
				</div>
				<p className="text-sm font-semibold text-foreground">
					تعذّر تحميل هذه الصفحة
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					Couldn't load this page
				</p>
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="mt-4 inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
				>
					إعادة المحاولة / Retry
				</button>
			</div>
		</div>
	);
}

function NotFoundPage() {
	const isAuthed = useAuthStore.getState().isAuthenticated();
	// A stale precached bundle won't know a route added in a newer deploy, so an
	// authenticated user hitting an unknown path is usually just running old JS.
	// Pull the fresh bundle once (guarded per-path so a genuine 404 can't loop)
	// — new-feature links then resolve instead of dead-ending here.
	useEffect(() => {
		if (!isAuthed) return;
		const key = `nf-reloaded:${window.location.pathname}`;
		if (sessionStorage.getItem(key)) return;
		sessionStorage.setItem(key, "1");
		refreshToLatest();
	}, [isAuthed]);
	return (
		<div className="min-h-[60vh] flex items-center justify-center p-6">
			<div className="w-full max-w-sm text-center rounded-2xl border border-border bg-card p-6 shadow-card">
				<div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950/40 text-brand-600 dark:text-brand-300">
					<Compass className="size-6" strokeWidth={2} aria-hidden />
				</div>
				<p className="text-sm font-semibold text-foreground">
					هذه الصفحة غير موجودة
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					الرابط غلط أو الصفحة انتقلت — Page not found
				</p>
				<Link
					to={isAuthed ? "/dashboard" : "/login"}
					className="mt-4 inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
				>
					{isAuthed ? "الرجوع للوحة التحكم" : "تسجيل الدخول"}
				</Link>
			</div>
		</div>
	);
}

export const router = createRouter({
	routeTree,
	// Lazily-loaded route chunks show this while fetching. defaultPendingMs
	// keeps fast loads from flashing the spinner.
	defaultPendingComponent: PageLoader,
	defaultPendingMs: 150,
	defaultErrorComponent: RouteError,
	// Unknown URLs get a styled in-shell page instead of the router's raw
	// English "Not Found" text.
	defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
